import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import BulkAssignForm from './BulkAssignForm.vue'
import BulkCancelForm from './BulkCancelForm.vue'
import BulkUnassignForm from './BulkUnassignForm.vue'
import BulkArchiveForm from './BulkArchiveForm.vue'
import BulkUpdateCostCenterForm from './BulkUpdateCostCenterForm.vue'
import BulkUpdateCustomFieldForm from './BulkUpdateCustomFieldForm.vue'
import BulkUpdateDestinationForm from './BulkUpdateDestinationForm.vue'
import BulkUpdateDetailsForm from './BulkUpdateDetailsForm.vue'
import BulkUpdatePickupForm from './BulkUpdatePickupForm.vue'
import BulkUpdatePriorityForm from './BulkUpdatePriorityForm.vue'
import BulkUpdateProjectForm from './BulkUpdateProjectForm.vue'
import BulkUpdateScheduleForm from './BulkUpdateScheduleForm.vue'
import BulkUpdateTagForm from '@/components/jobs/BulkUpdateTagForm.vue'
import BulkRemoveJobsFromBatchesForm from '@/components/jobs/BulkRemoveJobsFromBatchesForm.vue'
import { Component, computed, ref, watch } from 'vue'
import { sortBy, startCase } from 'lodash'
import {
  getBulkUpdatableFields,
  SimpleField,
} from '@/utils/field-schema-helper'
import Task from '@/models/task.js'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { useState, useStore } from 'vuex-composition-helpers'
import { ApprovalStatus, JobStatus } from '@/models/job'
import BulkAddJobsToBatchForm from '@/components/jobs/BulkAddJobsToBatchForm.vue'
import { useBusinessUnitPermissions } from '@/components/tickets/common/useBusinessUnitPermissions'

type DisabledReasons = {
  approved?: boolean
  cancelled?: boolean
  completed?: boolean
  inProgressOrFinal?: boolean
  multipleHubs?: boolean
  multipleResourceTypes?: boolean
  noInventory?: boolean
  userNoPermission?: boolean
  noJobsHaveBatches?: boolean
}

type FieldOption = {
  key: string
  name: string
  disabledText: string
  disabled: boolean
  onClick: () => void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BulkUpdateForm',
  props: {
    selectedJobs: null,
    allowAddToProject: { type: Boolean, default: true },
    allowAddToBatch: { type: Boolean, default: true },
    allowRemoveFromBatches: { type: Boolean, default: true }
  },
  emits: ["dismiss", "updated", "bottom-sheet-open", "bottom-sheet-closed"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'dismiss')
  (e: 'updated', updatedJobs: JobJson[])
  (e: 'bottom-sheet-open')
  (e: 'bottom-sheet-closed')
}), expose: any, slots: any, attrs: any }) {

const props = __props as { selectedJobs: JobJson[], allowAddToProject: boolean, allowAddToBatch: boolean, allowRemoveFromBatches: boolean }





const { currentOrgHasInvoicingEntitlement } = useBusinessUnitPermissions()
const store = useStore()
const canViewAddToBatchSelectionPage = computed<boolean>(
  () => store.getters['permissions/canViewAddToBatchSelectionPage']
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch']
)
const canEditBatchesOfSelectedJobs = computed(() => {
  let result = true
  for (const job of props.selectedJobs) {
    if (!canEditBatch.value(job.assigned_service_provider_id)) result = false
  }
  return result
})
const invoicesFeatureFlag = computed<boolean>(
  () => store.getters['featureFlags/invoices']
)

const selectedForm = ref<
  | 'add-to-project'
  | 'add-to-batch'
  | 'remove-from-batches'
  | 'cancel'
  | 'cost-center'
  | 'destination'
  | 'details'
  | 'pickup'
  | 'priority'
  | 'schedule'
  | 'custom-field'
  | 'tags'
  | 'assign'
  | 'unassign'
  | 'archive'
  | null
>()
const snackbarMessage = ref('')
const selectedCustomFieldKey = ref('')

const availableOptions = computed<FieldOption[]>(() => {
  const availableCoreOptions = coreFieldOptions.value.filter(
    (option) => !option.disabled
  )
  const availableCustomOptions = customFieldOptions.value.filter(
    (option) => !option.disabled
  )
  return sortBy(
    [...availableCoreOptions, ...availableCustomOptions],
    [(option) => option.name.toLowerCase()]
  )
})

const unavailableOptions = computed<FieldOption[]>(() => {
  const unavailableCoreOptions = coreFieldOptions.value.filter(
    (option) => option.disabled
  )
  const unavailableCustomOptions = customFieldOptions.value.filter(
    (option) => option.disabled
  )
  return sortBy(
    [...unavailableCoreOptions, ...unavailableCustomOptions],
    [(option) => option.name.toLowerCase()]
  )
})

const coreFieldOptions = computed<FieldOption[]>(() => {
  const optionsArray: FieldOption[] = [
    {
      key: 'assign',
      name: 'Bulk Assign',
      disabled:
        noJobsSelected.value ||
        anyJobsInProgressOrFinal.value ||
        jobsHaveDifferentResourceTypes.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('bulk assign', {
        inProgressOrFinal: anyJobsInProgressOrFinal.value,
        multipleResourceTypes: jobsHaveDifferentResourceTypes.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'assign'),
    },
    {
      key: 'unassign',
      name: 'Bulk Unassign',
      disabled:
        noJobsSelected.value ||
        anyJobsInProgressOrFinal.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('bulk unassign', {
        inProgressOrFinal: anyJobsInProgressOrFinal.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'unassign'),
    },
    {
      key: 'cost-center',
      name: 'Cost Center',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('cost center', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'cost-center'),
    },
    {
      key: 'destination',
      name: 'Destination',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('destination', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'destination'),
    },
    {
      key: 'details',
      name: 'Details',
      disabled:
        noJobsSelected.value ||
        anyJobsApproved.value ||
        anyJobsCancelled.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('details', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'details'),
    },
    {
      key: 'cancel',
      name: 'Mark as Cancelled',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('jobs to be cancelled', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'cancel'),
    },
    {
      key: 'pickup',
      name: 'Pickup Location',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        anyJobsHaveNoInventory.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('pickup location', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        noInventory: anyJobsHaveNoInventory.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'pickup'),
    },
    {
      key: 'priority',
      name: 'Priority',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('priority', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'priority'),
    },
    {
      key: 'schedule',
      name: 'Start Date/Time',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsCompleted.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('start date/time', {
        completed: anyJobsCompleted.value,
        cancelled: anyJobsCancelled.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'schedule'),
    },
    {
      key: 'tags',
      name: 'Tags',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        jobsBelongToMultipleHubs.value,
      disabledText: disabledToolTip('tags', {
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
      }),
      onClick: () => (selectedForm.value = 'tags'),
    },
    {
      key: 'archive',
      name: 'Archive',
      disabled: noJobsSelected.value || !userCanArchiveJob.value,
      disabledText: disabledToolTip('jobs to be archived', {
        userNoPermission: !userCanArchiveJob.value,
      }),
      onClick: () => (selectedForm.value = 'archive'),
    },
  ]
  if (props.allowAddToProject) {
    optionsArray.unshift({
      key: 'add-to-project',
      name: 'Add to Project',
      disabled:
        noJobsSelected.value ||
        anyJobsCancelled.value ||
        anyJobsApproved.value ||
        jobsBelongToMultipleHubs.value ||
        !userCanEditJob.value,
      disabledText: disabledToolTip('project', {
        approved: anyJobsApproved.value,
        cancelled: anyJobsCancelled.value,
        multipleHubs: jobsBelongToMultipleHubs.value,
        userNoPermission: !userCanEditJob.value,
      }),
      onClick: () => (selectedForm.value = 'add-to-project'),
    })
  }
  if (props.allowRemoveFromBatches && canEditBatchesOfSelectedJobs.value) {
    optionsArray.unshift({
      key: 'remove-from-batches',
      name: 'Remove from Batch',
      disabled: noJobsSelected.value || noJobsHaveBatches.value,
      disabledText: disabledToolTip('jobs to be removed from batch', {
        noJobsHaveBatches: noJobsHaveBatches.value,
      }),
      onClick: () => (selectedForm.value = 'remove-from-batches'),
    })
  }
  if (canViewAddToBatchSelectionPage.value && props.allowAddToBatch) {
    optionsArray.unshift({
      key: 'add-to-batch',
      name: 'Add to Batch',
      disabled: noJobsSelected.value || jobsBelongToMultipleHubs.value,
      disabledText: disabledToolTip('jobs to be added to batch', {
        multipleHubs: jobsBelongToMultipleHubs.value,
      }),
      onClick: () => (selectedForm.value = 'add-to-batch'),
    })
  }
  return optionsArray
})

const customFieldOptions = computed<FieldOption[]>(() => {
  return updatableCustomFields.value.map((customField) => ({
    key: customField.key,
    name: customField.definition.title,
    disabled:
      anyJobsApproved.value ||
      anyJobsCancelled.value ||
      jobsBelongToMultipleHubs.value ||
      !userCanEditJob.value,
    disabledText: disabledToolTip(customField.definition.title, {
      approved: anyJobsApproved.value,
      cancelled: anyJobsCancelled.value,
      multipleHubs: jobsBelongToMultipleHubs.value,
      userNoPermission: !userCanEditJob.value,
    }),
    onClick: () => handleCustomFieldClicked(customField.key),
  }))
})

const coreFieldFormComponent = computed<Component | null>(() => {
  if (selectedForm.value === 'add-to-project') {
    return BulkUpdateProjectForm
  } else if (
    canViewAddToBatchSelectionPage.value &&
    selectedForm.value === 'add-to-batch'
  ) {
    return BulkAddJobsToBatchForm
  } else if (
    invoicesFeatureFlag.value &&
    currentOrgHasInvoicingEntitlement.value &&
    selectedForm.value === 'remove-from-batches'
  ) {
    return BulkRemoveJobsFromBatchesForm
  } else if (selectedForm.value === 'details') {
    return BulkUpdateDetailsForm
  } else if (selectedForm.value === 'priority') {
    return BulkUpdatePriorityForm
  } else if (selectedForm.value === 'schedule') {
    return BulkUpdateScheduleForm
  } else if (selectedForm.value === 'destination') {
    return BulkUpdateDestinationForm
  } else if (selectedForm.value === 'pickup') {
    return BulkUpdatePickupForm
  } else if (selectedForm.value === 'cost-center') {
    return BulkUpdateCostCenterForm
  } else if (selectedForm.value === 'cancel') {
    return BulkCancelForm
  } else if (selectedForm.value === 'tags') {
    return BulkUpdateTagForm
  } else if (selectedForm.value === 'assign') {
    return BulkAssignForm
  } else if (selectedForm.value === 'unassign') {
    return BulkUnassignForm
  } else if (selectedForm.value === 'archive') {
    return BulkArchiveForm
  }
  return null
})

const customFieldFormComponent = computed<Component | null>(() => {
  if (selectedForm.value === 'custom-field') {
    return BulkUpdateCustomFieldForm
  }

  return null
})

const updatableCustomFields = computed<SimpleField[]>(() => {
  const activityIds = props.selectedJobs.map((job) => job.activity_id)
  const { all: activities } = useState('tasks', ['all'])
  const activitySchemas = Object.values(activities.value)
    .filter((activity: Task) => activityIds.includes(activity.id))
    .map((activity: Task) => activity.jobCustomFieldsSchema)

  const { jobCustomFieldsSchema: referenceSchema } = useState('schemas', [
    'jobCustomFieldsSchema',
  ])

  return getBulkUpdatableFields(
    activitySchemas,
    referenceSchema.value.schema.definitions
  )
})

const selectedCustomField = computed(() => {
  return updatableCustomFields.value.find(
    (field) => field.key === selectedCustomFieldKey.value
  )
})

const handleCustomFieldClicked = (customFieldKey: string) => {
  selectedForm.value = 'custom-field'
  selectedCustomFieldKey.value = customFieldKey
}

const handleUpdate = ({ message, updatedJobs }: BulkUpdateResponse) => {
  closeForm()
  snackbarMessage.value = message
  emits('updated', updatedJobs)
}

watch(selectedForm, (selection) => {
  if (selection != null) {
    emits('bottom-sheet-open')
  } else {
    emits('bottom-sheet-closed')
  }
})

const closeForm = () => (selectedForm.value = null)
const clearSnackbarMessage = () => (snackbarMessage.value = '')
const dismiss = () => emits('dismiss')

const formDescription = computed(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2
    ? `Choose a field to update for the ${jobCount} selected jobs`
    : 'Choose a field to update for the selected job'
})

const updateJobsLabel = computed(() => {
  const jobCount = props.selectedJobs.length
  return jobCount >= 2 ? `Update ${jobCount} jobs` : 'Update job'
})

const anyJobsApproved = computed(() => {
  return props.selectedJobs.some((j) => j.approval_status === 'Approved')
})
const approvedJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.status === 'Completed').length
})

const anyJobsCompleted = computed(() => {
  return props.selectedJobs.some((j) => j.status === 'Completed')
})
const completedJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.status === 'Completed').length
})

const anyJobsCancelled = computed(() => {
  return props.selectedJobs.some((j) => j.status === 'Cancelled')
})
const cancelledJobCount = computed(() => {
  return props.selectedJobs.filter((j) => j.status === 'Cancelled').length
})

const anyJobsHaveNoInventory = computed(() => {
  return props.selectedJobs.some((j) => j.inventory_requirements.length === 0)
})
const jobsWithNoInventoryCount = computed<number>(() => {
  return props.selectedJobs.filter((j) => j.inventory_requirements.length === 0)
    .length
})

const jobsBelongToMultipleHubs = computed(() => {
  const hubs = new Set(props.selectedJobs.map((j) => j.hub_id))
  return [...hubs].length > 1
})

const noJobsHaveBatches = computed(() =>
  props.selectedJobs.every((j) => !j.invoice_id)
)

const noJobsSelected = computed(() => props.selectedJobs.length === 0)

const anyJobsInProgressOrFinal = computed(() => {
  return props.selectedJobs.some(
    (j) =>
      j.status !== 'Requested' &&
      j.status !== 'AssignedToServiceProvider' &&
      j.status !== 'Scheduled' &&
      j.status !== 'AssignedToPool'
  )
})
const jobsInProgressOrFinalCount = computed(() => {
  return props.selectedJobs.filter(
    (j) =>
      j.status !== 'Requested' &&
      j.status !== 'AssignedToServiceProvider' &&
      j.status !== 'Scheduled' &&
      j.status !== 'AssignedToPool'
  ).length
})

const jobsHaveDifferentResourceTypes = computed(() => {
  const selectedResourceTypes: Set<number> = new Set(
    props.selectedJobs.map((job) => job.resource_type_id)
  )
  return selectedResourceTypes.size > 1
})

const disabledToolTip = (
  field: string,
  disabledReasons: DisabledReasons
): string => {
  let message = `Editing ${field} unavailable: \n`

  if (noJobsSelected.value) {
    message += 'No jobs selected'
    return message
  }

  if (disabledReasons.approved) {
    const count = approvedJobCount.value
    message += `${count} ${count > 1 ? 'jobs are' : 'job is'} approved\n`
  }

  if (disabledReasons.inProgressOrFinal) {
    const count = jobsInProgressOrFinalCount.value
    message += `${count} ${
      count > 1 ? 'jobs are' : 'job is'
    } in progress or completed\n`
  }

  if (disabledReasons.completed) {
    const count = completedJobCount.value
    message += `${count} ${count > 1 ? 'jobs are' : 'job is'} completed\n`
  }

  if (disabledReasons.cancelled) {
    const count = cancelledJobCount.value
    message += `${count} ${count > 1 ? 'jobs are' : 'job is'} cancelled\n`
  }

  if (disabledReasons.noInventory) {
    const count = jobsWithNoInventoryCount.value
    message += `${count} ${count > 1 ? 'jobs have' : 'job has'} no inventory\n`
  }

  if (disabledReasons.multipleHubs) {
    message += 'Jobs belong to multiple hubs\n'
  }

  if (disabledReasons.multipleResourceTypes) {
    message += 'Jobs have multiple resource types\n'
  }

  if (disabledReasons.userNoPermission) {
    message += "You don't have permission to edit jobs\n"
  }

  if (disabledReasons.noJobsHaveBatches) {
    message += 'No selected jobs belong to an batch\n'
  }

  return message
}

const canModifyCompletedJobsPendingApproval = (hubId: string) =>
  store.getters['permissions/canModifyCompletedJobsPendingApproval'](
    hubId
  ) as boolean
const canModifyCompletedJobsWithNoApproval = (hubId: string) =>
  store.getters['permissions/canModifyCompletedJobsWithNoApproval'](
    hubId
  ) as boolean

const canArchiveJob = (hubId: string, accountId: string) =>
  store.getters['permissions/canArchiveJob'](hubId, accountId) as boolean

const userCanArchiveJob = computed(() => {
  return props.selectedJobs.every((j) => canArchiveJob(j.hub_id, j.account_id))
})

const userCanEditJob = computed(() => {
  let result = true
  for (const job of props.selectedJobs) {
    if (job.status !== JobStatus.COMPLETED) {
      continue
    }
    if (job.approval_status === ApprovalStatus.PENDING_APPROVAL) {
      result &&= canModifyCompletedJobsPendingApproval(job.hub_id)
    } else if (job.approval_status === ApprovalStatus.NO_APPROVAL) {
      result &&= canModifyCompletedJobsWithNoApproval(job.hub_id)
    } else if (job.approval_status === ApprovalStatus.APPROVED) {
      result = false
    }
    if (!result) {
      break
    }
  }
  return result
})

return { __sfc: true,emits, props, currentOrgHasInvoicingEntitlement, store, canViewAddToBatchSelectionPage, canEditBatch, canEditBatchesOfSelectedJobs, invoicesFeatureFlag, selectedForm, snackbarMessage, selectedCustomFieldKey, availableOptions, unavailableOptions, coreFieldOptions, customFieldOptions, coreFieldFormComponent, customFieldFormComponent, updatableCustomFields, selectedCustomField, handleCustomFieldClicked, handleUpdate, closeForm, clearSnackbarMessage, dismiss, formDescription, updateJobsLabel, anyJobsApproved, approvedJobCount, anyJobsCompleted, completedJobCount, anyJobsCancelled, cancelledJobCount, anyJobsHaveNoInventory, jobsWithNoInventoryCount, jobsBelongToMultipleHubs, noJobsHaveBatches, noJobsSelected, anyJobsInProgressOrFinal, jobsInProgressOrFinalCount, jobsHaveDifferentResourceTypes, disabledToolTip, canModifyCompletedJobsPendingApproval, canModifyCompletedJobsWithNoApproval, canArchiveJob, userCanArchiveJob, userCanEditJob, FormLayout, startCase }
}

})